import React, { useState, useEffect } from 'react';
import { vendorTypesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Plus, Pencil, Trash2, PowerOff, Power, Loader2 } from 'lucide-react';

/**
 * Admin-managed VendorType lookup editor.
 *
 * Rules enforced by the backend:
 *  - Types currently referenced by any vendor cannot be hard-deleted (409).
 *    UI catches the 409 and offers a "Deactivate instead" fallback.
 *  - Unused types can be deleted outright.
 *  - Deactivating preserves existing vendor tags (historical fact).
 */
export default function VendorTypesDialog({ open, onOpenChange, onSaved }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await vendorTypesAPI.list();
      setTypes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendor types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setCreating(true);
    try {
      await vendorTypesAPI.create({ label });
      toast.success(`Added "${label}"`);
      setNewLabel('');
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add vendor type');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (t) => {
    const label = renameValue.trim();
    if (!label || label === t.label) { setRenamingId(null); return; }
    try {
      await vendorTypesAPI.rename(t.id, { label });
      toast.success('Renamed');
      setRenamingId(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    }
  };

  const toggleActive = async (t) => {
    try {
      await vendorTypesAPI.setActive(t.id, !t.is_active);
      toast.success(t.is_active ? 'Deactivated' : 'Activated');
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change active state');
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete vendor type "${t.label}"? This cannot be undone.`)) return;
    try {
      await vendorTypesAPI.delete(t.id);
      toast.success('Deleted');
      await load();
      onSaved?.();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409) {
        // In-use — offer deactivate
        if (window.confirm(`${detail}\n\nDeactivate "${t.label}" instead?`)) {
          try {
            await vendorTypesAPI.setActive(t.id, false);
            toast.success('Deactivated');
            await load();
            onSaved?.();
          } catch (e2) {
            toast.error(e2.response?.data?.detail || 'Deactivate failed');
          }
        }
      } else {
        toast.error(detail || 'Delete failed');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="vendor-types-dialog">
        <DialogHeader>
          <DialogTitle>Manage Vendor Types</DialogTitle>
          <DialogDescription>
            Types in use cannot be hard-deleted — deactivate to hide them from the
            vendor form while preserving historical tags.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 pt-2">
          <Input
            placeholder="New vendor type label (e.g. Uniforms Supplier)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            data-testid="vt-new-label-input"
          />
          <Button
            onClick={handleCreate}
            disabled={creating || !newLabel.trim()}
            data-testid="vt-add-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="ml-2">Add</span>
          </Button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto border rounded divide-y">
          {loading && (
            <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
          )}
          {!loading && types.length === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm">No vendor types defined</div>
          )}
          {!loading && types.map((t) => (
            <div key={t.id} className="flex items-center gap-2 p-3" data-testid={`vt-row-${t.id}`}>
              {renamingId === t.id ? (
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename(t);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  onBlur={() => handleRename(t)}
                  data-testid={`vt-rename-input-${t.id}`}
                />
              ) : (
                <span className="flex-1 text-sm font-medium">{t.label}</span>
              )}
              <Badge variant={t.is_active ? 'default' : 'secondary'} className="text-xs">
                {t.is_active ? 'Active' : 'Inactive'}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setRenamingId(t.id); setRenameValue(t.label); }}
                title="Rename"
                data-testid={`vt-rename-btn-${t.id}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleActive(t)}
                title={t.is_active ? 'Deactivate' : 'Activate'}
                data-testid={`vt-toggle-btn-${t.id}`}
              >
                {t.is_active ? <PowerOff className="w-4 h-4 text-amber-600" /> : <Power className="w-4 h-4 text-emerald-600" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(t)}
                title="Delete (only if unused)"
                data-testid={`vt-delete-btn-${t.id}`}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
